import React, { useMemo, useState } from 'react';
import { useEditor } from '../../context/EditorContext';
import { WpDialog } from './DialogShell';
import { planAutoflow, AUTOFLOW_MAX_PAGES } from '../../lib/storyFlow';
import { documentObjects, locateObject, locationLabel } from '../../lib/storyChain';
import { Checkbox } from '../ui/checkbox';
import { FilePlus2, Link2, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

const pageList = (indexes, pages) => {
  const names = [...new Set(indexes)].sort((a, b) => a - b).map((i) => pages[i]?.name || `Page ${i + 1}`);
  if (names.length <= 1) return names[0] || '';
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
};

/**
 * Publisher's "text in overflow" prompt. props: { objectId }
 *   - Autoflow: first into empty text boxes that already exist after this one (Publisher asks the same), then onto new
 *     pages after the last box used, each with a linked text box the size of this one, until the story fits.
 *   - Link manually: pick an empty text box on any page (link mode survives switching pages).
 */
export const AutoflowDialog = ({ props }) => {
  const { state, dispatch, rawDispatch, closeDialog } = useEditor();
  const objectId = props.objectId;
  const [useExisting, setUseExisting] = useState(true);
  const doc = useMemo(() => ({ pages: state.pages, scratch: state.scratch, textFlow: state.textFlow, currentPageIndex: state.currentPageIndex }), [state.pages, state.scratch, state.textFlow, state.currentPageIndex]);
  const plan = useMemo(() => planAutoflow(doc, objectId, { useExisting }), [doc, objectId, useExisting]);
  const box = useMemo(() => documentObjects(state.pages, state.scratch).find((o) => o.id === objectId), [state.pages, state.scratch, objectId]);
  const where = locationLabel(locateObject(objectId, state.pages, state.scratch), state.pages);

  if (!box || !plan) {
    return (
      <WpDialog name="autoflow" title="Autoflow" width="w-[440px]" onCancel={closeDialog} cancelLabel="Close">
        <p className="text-sm text-muted-foreground" data-testid="autoflow-unavailable">This text box already continues into another box. Select the last box of the story to autoflow from there.</p>
      </WpDialog>
    );
  }
  if (!plan.overflowWords) {
    return (
      <WpDialog name="autoflow" title="Autoflow" width="w-[440px]" onCancel={closeDialog} cancelLabel="Close">
        <p className="flex items-center gap-2 text-sm" data-testid="autoflow-fits"><CheckCircle2 className="h-4 w-4 text-emerald-600" aria-hidden="true" /> Everything fits in this text box — there is nothing to flow on.</p>
      </WpDialog>
    );
  }
  const n = plan.additions.length;
  const f = plan.fills.length;
  const words = plan.overflowWords;
  const fillText = f ? `the ${f === 1 ? 'empty text box' : `${f} empty text boxes`} on ${pageList(plan.fills.map((x) => x.pageIndex), state.pages)}` : '';
  const addText = n ? `${n} page${n === 1 ? '' : 's'} after ${f ? pageList([plan.fills[plan.fills.length - 1].pageIndex], state.pages) : where}, each with a linked text box the same size and position as this one` : '';
  let summary;
  if (f && n) summary = `Fills ${fillText} first, then adds ${addText}, until the whole story fits.`;
  else if (f) summary = `Fills ${fillText} — the story fits without adding pages.`;
  else summary = `Adds ${addText}, until the whole story fits.`;
  if (plan.capped) summary += ` (Stops at ${AUTOFLOW_MAX_PAGES} new pages.)`;

  const run = () => {
    dispatch({ type: 'AUTOFLOW_STORY', payload: plan });
    closeDialog();
    const parts = [];
    if (f) parts.push(`used ${f} existing text box${f === 1 ? '' : 'es'}`);
    if (n) parts.push(`added ${n} page${n === 1 ? '' : 's'}`);
    toast.success(`Autoflow ${parts.join(' and ')}`, {
      description: plan.capped
        ? `The story still does not fit after ${AUTOFLOW_MAX_PAGES} new pages — run Autoflow again from the last box, or enlarge the boxes.`
        : 'The story now continues in linked text boxes. Select any of them to see the Story Map.',
      action: { label: 'Go to last box', onClick: () => dispatch({ type: 'SELECT_OBJECT_ANYWHERE', payload: plan.lastBoxId }) },
      duration: 8000,
    });
  };
  const manual = () => {
    rawDispatch({ type: 'SET_PENDING_LINK', payload: objectId });
    closeDialog();
    toast.info('Click an empty text box to continue the story there', { id: 'link-mode', description: 'On this page or any other — use the Pages pane to switch pages. Esc cancels.', duration: 6000 });
  };
  return (
    <WpDialog name="autoflow" title="Text does not fit" description={`${words} word${words === 1 ? '' : 's'} of this story ${words === 1 ? 'is' : 'are'} in overflow (text box on ${where}).`} width="w-[500px]" onCancel={closeDialog} cancelLabel="Cancel">
      <div className="flex flex-col gap-2" data-testid="autoflow-dialog-body">
        <button type="button" onClick={run} className="group flex items-start gap-3 rounded-md border border-primary/40 bg-primary/5 p-3 text-left hover:bg-primary/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" data-testid="autoflow-run-button">
          <FilePlus2 className="h-5 w-5 mt-0.5 text-primary shrink-0" aria-hidden="true" />
          <span className="min-w-0">
            <span className="block text-sm font-semibold text-foreground">Autoflow</span>
            <span className="block text-xs text-muted-foreground" data-testid="autoflow-summary">{summary}</span>
          </span>
        </button>
        {plan.candidates > 0 && (
          <label className="flex items-start gap-2 px-1 py-0.5 text-xs text-muted-foreground cursor-pointer select-none" data-testid="autoflow-use-existing">
            <Checkbox checked={useExisting} onCheckedChange={(v) => setUseExisting(!!v)} className="mt-0.5" data-testid="autoflow-use-existing-checkbox" aria-label="Use empty text boxes on later pages first" />
            <span>Use the {plan.candidates === 1 ? 'empty text box' : `${plan.candidates} empty text boxes`} already on later pages first, before adding new pages (what Publisher asks).</span>
          </label>
        )}
        <button type="button" onClick={manual} className="group flex items-start gap-3 rounded-md border border-border p-3 text-left hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" data-testid="autoflow-manual-button">
          <Link2 className="h-5 w-5 mt-0.5 text-muted-foreground shrink-0" aria-hidden="true" />
          <span className="min-w-0">
            <span className="block text-sm font-semibold text-foreground">Link to a text box I choose</span>
            <span className="block text-xs text-muted-foreground">Pick an empty text box — on this page or on another page — and the overflow continues there.</span>
          </span>
        </button>
      </div>
    </WpDialog>
  );
};
